/**
 * Doserne Daily Progress — upload photos to a project, tagged with a chosen date.
 * Allows backdating up to 30 days.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Camera, ImagePlus, Trash2, Calendar, ChevronDown, X, Send, Check,
  Package, Plus,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";
import DoserneProgressTimeline from "./DoserneProgressTimeline";
import CameraCapture from "./CameraCapture";
import LagerPickerModal from "./LagerPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysBack(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("no-NO", { weekday: "long", day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function DoserneProgress() {
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const [searchParams] = useSearchParams();
  const prefillProject = searchParams.get("project_id") || "";
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(prefillProject);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [materials, setMaterials] = useState([]); // [{ item_id, name, quantity, unit }]
  const [showLager, setShowLager] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const { data } = await axios.get(`${API}/doserne/projects?status=active`, authHeader());
        setProjects(data.projects || []);
      } catch { /* silent */ }
    })();
  }, [user, authHeader]);

  // Load scaffold jobs for the chosen project (to tag the progress).
  useEffect(() => {
    setJobId("");
    setJobs([]);
    if (!user || !projectId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/projects/${projectId}/jobs`, authHeader());
        setJobs(data.jobs || []);
      } catch { /* silent */ }
    })();
  }, [user, projectId, authHeader]);

  const dateOptions = useMemo(() => {
    const out = [];
    for (let i = 0; i <= 14; i++) out.push({ v: daysBack(i), label: i === 0 ? "I dag" : i === 1 ? "I går" : `${i} dager siden — ${fmt(daysBack(i))}` });
    return out;
  }, []);

  const onFiles = (list) => {
    const arr = Array.from(list || []);
    const next = [...files, ...arr].slice(0, 10); // max 10 photos per post
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const removeFile = (i) => {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const addFromLager = (pick) => {
    setMaterials((prev) => {
      const idx = prev.findIndex((m) => m.item_id === pick.item_id);
      if (idx >= 0) return prev.map((m, i) => i === idx ? { ...m, quantity: m.quantity + pick.quantity } : m);
      if (prev.length >= 50) return prev;
      return [...prev, pick];
    });
  };

  const removeMaterial = (itemId) => {
    setMaterials((prev) => prev.filter((m) => m.item_id !== itemId));
  };
  const selectedMap = materials.reduce((acc, m) => { acc[m.item_id] = m.quantity; return acc; }, {});

  const submit = async (e) => {
    e.preventDefault();
    if (!projectId) return toast.error("Velg et prosjekt");
    if (!files.length && !note.trim() && !materials.length) {
      return toast.error("Legg til minst ett bilde, en notat eller et materiale");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("progress_date", date);
      fd.append("note", note);
      if (materials.length) {
        fd.append("materials_json", JSON.stringify(materials));
      }
      if (jobId) fd.append("job_id", jobId);
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/doserne/progress`, fd, {
        ...authHeader(),
        headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Progresjon publisert! 🎉");
      setNote("");
      setFiles([]);
      setPreviews([]);
      setMaterials([]);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Feil ved publisering");
    }
    setBusy(false);
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell title="Daglig progresjon" subtitle="Legg til bilder og notater fra jobben — kategoriseres automatisk per dag i prosjektet.">
      {/* Upload form */}
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-6" data-testid="progress-form">
        {/* Project + date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Prosjekt *</label>
            <select
              required
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
              data-testid="progress-project"
            >
              <option value="">— Velg prosjekt —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.number ? `${p.number} · ` : ""}{p.address || p.name}{p.name && p.address && p.name !== p.address ? ` (${p.name})` : ""}</option>
              ))}
            </select>
            {projects.length === 0 && (
              <div className="text-xs text-slate-500 mt-1">
                Ingen aktive prosjekter. <Link to="/doserne/prosjekter" className="text-cyan-600 hover:underline">Legg til ett først</Link>.
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Dato</label>
            <div className="relative mt-1">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full pl-9 pr-8 py-2.5 border border-slate-300 rounded-lg text-sm bg-white appearance-none"
                data-testid="progress-date"
              >
                {dateOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Optional job link */}
        {jobs.length > 0 && (
          <div className="mb-3">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Jobb (valgfritt)</label>
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
              data-testid="progress-job"
            >
              <option value="">— Hele prosjektet —</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </div>
        )}

        {/* Note */}
        <div className="mb-3">
          <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Notat (valgfritt)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Kort beskrivelse av hva som ble gjort…"
            className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="progress-note"
          />
        </div>

        {/* Materials from Lager — catalog-only picker (identical to mobile).
            Mirrored into the project's material list, linked to real stock. */}
        <div className="mb-3">
          <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" /> Materialer fra lager ({materials.length})
          </label>
          <button
            type="button"
            onClick={() => setShowLager(true)}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-cyan-50 border border-cyan-200 hover:bg-cyan-100 text-left transition"
            data-testid="progress-material-lager"
          >
            <Package className="w-4 h-4 text-cyan-700 flex-shrink-0" />
            <span className="text-sm font-black text-cyan-800 flex-1">Adaugă din Lager</span>
            <Plus className="w-4 h-4 text-cyan-600 flex-shrink-0" />
          </button>
          {materials.length > 0 && (
            <ul className="mt-2 space-y-1.5" data-testid="progress-materials-list">
              {materials.map((m) => (
                <li
                  key={m.item_id}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg"
                  data-testid={`progress-material-${m.item_id}`}
                >
                  <Package className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                  <span className="flex-1 text-sm text-slate-800 truncate">{m.name}</span>
                  <span className="text-xs font-black text-cyan-700">×{m.quantity} {m.unit}</span>
                  <button
                    type="button"
                    onClick={() => removeMaterial(m.item_id)}
                    className="text-slate-400 hover:text-red-500 p-1"
                    aria-label="Fjern materiale"
                    data-testid={`progress-material-remove-${m.item_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Photos */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Bilder ({files.length}/10)</label>
            {files.length < 10 && (
              <button
                type="button"
                onClick={() => setCameraOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-xs font-black"
                style={{ color: "#fff" }}
                data-testid="progress-open-camera"
              >
                <Camera className="w-3.5 h-3.5" /> Åpne kamera
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => onFiles(e.target.files)}
            className="hidden"
            data-testid="progress-file-input"
          />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
            {previews.map((src, i) => (
              <div key={i} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-900/80 flex items-center justify-center"
                  style={{ color: "#fff" }}
                  data-testid={`remove-photo-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {files.length < 10 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:text-slate-900 hover:border-slate-500 active:bg-slate-50"
                data-testid="progress-add-photo"
              >
                <ImagePlus className="w-6 h-6 mb-1" />
                <span className="text-[10px] font-semibold uppercase">Legg til</span>
              </button>
            )}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={busy || !projectId}
          className="mt-4 w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 rounded-lg font-semibold text-sm shadow-sm flex items-center justify-center gap-2 transition"
          style={{ color: "#fff" }}
          data-testid="progress-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Publiser</>}
        </button>
      </form>

      {/* Timeline for the selected project (or a hint) */}
      {projectId ? (
        <>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">
            Tidligere innlegg — dette prosjektet
          </div>
          <DoserneProgressTimeline key={refreshKey} projectId={projectId} authHeader={authHeader} currentUserId={user.id} isAdmin={user.is_admin} />
        </>
      ) : (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center text-sm text-slate-500">
          Velg et prosjekt over for å se tidligere innlegg.
        </div>
      )}

      {cameraOpen && (
        <CameraCapture
          maxPhotos={Math.max(1, 10 - files.length)}
          onCancel={() => setCameraOpen(false)}
          onDone={(newFiles) => {
            setCameraOpen(false);
            if (newFiles.length) onFiles(newFiles);
          }}
        />
      )}

      <LagerPickerModal open={showLager} onClose={() => setShowLager(false)} authHeader={authHeader} selected={selectedMap} onAdd={addFromLager} />
    </DoserneShell>
  );
}
